import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { canonicalModuleOfferLocator, isApiError, sameModuleOfferLocator } from "@bw/shared/api";
import { useQueryClient } from "@tanstack/react-query";
import { useMyOrg } from "@/hooks/use-my-org";
import { useModuleCommitment } from "./useModuleOffers";
import { ModuleOfferDocument } from "./ModuleOfferContent";
import { ModuleOfferLifecycleTerms } from "./ModuleOfferLifecycleTerms";
import { ModuleOfferSummary } from "./ModuleOfferSummary";
import { ModuleAgreementBilling } from "./ModuleAgreementBilling";
import { ModuleAgreementPurchase } from "./ModuleAgreementPurchase";
import { ModuleAgreementReceipts } from "./ModuleAgreementReceipts";
import { ModuleAgreementExpiry } from "./ModuleAgreementExpiry";
import type { CompanyExpirySelection, CompanyExpiryDenial, CompanyExpiryRevalidation, CompanyPurchaseExpirySelectionEvent } from "./companyExpiryContracts";
import { companyExpiryQueryKey } from "./useModuleExpiry";
import type { CompanyBillingDenial, CompanyBillingRevalidation } from "./companyMethodContracts";
import type { CompanyPurchaseRecordSelection, CompanyPurchaseRecordsSelectionEvent } from "./companyReceiptContracts";
import { uuid } from "./useModuleBilling";
import {
  ModuleOfferReadError,
  ModuleOfferWorkspace,
} from "./ModuleOfferWorkspace";

function AgreementController({ orgId, acceptanceId, actorId }: { orgId: string; acceptanceId: string; actorId: string }) {
  const queryClient = useQueryClient();
  const query = useModuleCommitment(orgId, acceptanceId);
  const copy = query.data;
  const [denied, setDenied] = useState(false);
  const [billingFenceRevision, setBillingFenceRevision] = useState(0);
  const [recordsSelection, setRecordsSelection] = useState<CompanyPurchaseRecordSelection | null>(null);
  const [expirySelection, setExpirySelection] = useState<CompanyExpirySelection | null>(null);
  const [expiryFenceRevision, setExpiryFenceRevision] = useState(0);
  const expiryTransition = useRef({ revision: 0, latched: false });
  const expiryOriginal = useRef(expirySelection); expiryOriginal.current = expirySelection;
  const billingTransition = useRef({ revision: 0, latched: false });
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const readDenied = query.isError && isApiError(query.error) && [401, 403].includes(query.error.status);
  useEffect(() => { if (readDenied) setDenied(true); }, [readDenied]);
  async function retryCopy() {
    const result = await query.refetch();
    if (live.current && result.isSuccess && sameModuleOfferLocator(result.data.org_id, orgId)
      && sameModuleOfferLocator(result.data.acceptance_id, acceptanceId)) setDenied(false);
  }
  function billingDenied(event: CompanyBillingDenial) {
    if (!live.current || event.scope.actorId !== actorId || event.scope.orgId !== orgId
      || event.scope.acceptanceId !== acceptanceId || event.scope.environment !== copy?.offer.environment) return;
    if (event.category === "new_admission_restricted") return;
    if (event.category === "identity_or_scope_lost") setDenied(true);
    if (billingTransition.current.latched) return;
    const revision = billingTransition.current.revision + 1;
    billingTransition.current = { revision, latched: true };
    setBillingFenceRevision(revision);
    const queryKey = ["company-software", actorId, orgId, event.scope.environment, acceptanceId];
    void queryClient.cancelQueries({ queryKey }); queryClient.removeQueries({ queryKey });
    if (event.category === "identity_or_scope_lost") return;
    // manage_billing denial is not read_terms denial. Only the exact parent read
    // can deny this copy; its success cannot clear a child write-denial latch.
    void query.refetch().then(result => {
      if (live.current && result.isError && isApiError(result.error) && [401, 403].includes(result.error.status)) setDenied(true);
    });
  }
  function billingRevalidated(event: CompanyBillingRevalidation) {
    if (!live.current || event.scope.actorId !== actorId || event.scope.orgId !== orgId
      || event.scope.acceptanceId !== acceptanceId || event.scope.environment !== copy?.offer.environment
      || event.billingFenceRevision !== billingTransition.current.revision) return;
    // Only explicit matching child billing review re-arms a future loss. It
    // does not acknowledge the other child or restore a financial action ban.
    billingTransition.current.latched = false;
  }
  function viewPurchaseRecords(event: CompanyPurchaseRecordsSelectionEvent) {
    if (!live.current || billingTransition.current.latched || event.billingFenceRevision !== billingTransition.current.revision
      || event.scope.actorId !== actorId || event.scope.orgId !== orgId || event.scope.acceptanceId !== acceptanceId
      || event.scope.environment !== copy?.offer.environment || event.original.acceptance_id !== acceptanceId
      || ![event.original.purchase_id, event.original.operation_id, event.original.acceptance_id].every(uuid)
      || !Number.isFinite(Date.parse(event.original.created_at))) return;
    // Read-only records selection does not change any money original, PI or
    // unresolved browser command owned by the still-mounted purchase sibling.
    setRecordsSelection(event.original);
  }
  function reviewExpiry(event: CompanyPurchaseExpirySelectionEvent) {
    if (!live.current || event.expiryFenceRevision !== expiryTransition.current.revision
      || event.scope.actorId !== actorId || !sameModuleOfferLocator(event.scope.orgId, orgId)
      || !sameModuleOfferLocator(event.scope.acceptanceId, acceptanceId) || event.scope.environment !== copy?.offer.environment
      || !sameModuleOfferLocator(event.original.acceptance_id, acceptanceId)
      || ![event.original.purchase_id, event.original.operation_id, event.original.acceptance_id].every(uuid)
      || !Number.isFinite(Date.parse(event.original.created_at))) return;
    // Read-only target, independent of selected money/receipt original and PI.
    setExpirySelection(event.original);
  }
  function expiryDenied(event: CompanyExpiryDenial) {
    if (!live.current || event.expiryFenceRevision !== expiryTransition.current.revision
      || event.scope.actorId !== actorId || !sameModuleOfferLocator(event.scope.orgId, orgId)
      || !sameModuleOfferLocator(event.scope.acceptanceId, acceptanceId) || event.scope.environment !== copy?.offer.environment
      || !sameModuleOfferLocator(event.purchaseId, expiryOriginal.current?.purchase_id)) return;
    if (event.category === "identity_or_scope_lost") billingDenied({ scope: event.scope, operation: event.operation, category: event.category });
    if (expiryTransition.current.latched) return;
    const revision = expiryTransition.current.revision + 1;
    expiryTransition.current = { revision, latched: true }; setExpiryFenceRevision(revision);
    const queryKey = companyExpiryQueryKey(event.scope);
    void queryClient.cancelQueries({ queryKey }); queryClient.removeQueries({ queryKey });
  }
  function expiryRevalidated(event: CompanyExpiryRevalidation) {
    if (!live.current || event.operation !== "getExpiryReview" || event.expiryFenceRevision !== expiryTransition.current.revision
      || event.scope.actorId !== actorId || !sameModuleOfferLocator(event.scope.orgId, orgId)
      || !sameModuleOfferLocator(event.scope.acceptanceId, acceptanceId) || event.scope.environment !== copy?.offer.environment
      || !sameModuleOfferLocator(event.purchaseId, expiryOriginal.current?.purchase_id)) return;
    // Only this explicit purpose read re-arms future loss; no sibling ack.
    expiryTransition.current.latched = false;
  }
  if (denied || readDenied) return <ModuleOfferReadError onRetry={() => void retryCopy()} />;
  if (query.isPending) return <Typography role="status">Loading accepted copy…</Typography>;
  if (query.isError || !copy || !sameModuleOfferLocator(copy.org_id, orgId) || !sameModuleOfferLocator(copy.acceptance_id, acceptanceId))
    return <ModuleOfferReadError onRetry={() => void retryCopy()} />;
  return <Stack spacing={2}>
    <Box component="section" role="region" aria-label="Accepted offer summary">
      <Stack spacing={1}>
        <ModuleOfferSummary offer={copy.offer} action={false} />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ alignItems: { sm: "center" } }}>
          <Chip size="small" color="success" label="Accepted" sx={{ alignSelf: { xs: "flex-start" } }} />
          <Typography variant="body2" color="text.secondary">
            {new Date(copy.accepted_at).toLocaleString()}{copy.accepted_by_display_name ? ` · ${copy.accepted_by_display_name}` : ""}
          </Typography>
        </Stack>
      </Stack>
    </Box>
    <Paper component="section" role="region" aria-label="Accepted agreement" variant="outlined"
      sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
      <ModuleOfferDocument document={copy.offer.document} headingLevel="h2" />
    </Paper>
    {copy.offer.terms_format === "software_terms_v2" && copy.offer.publication_format === "module_policy_v1" && copy.offer.lifecycle_terms
      ? <Paper component="section" role="region" aria-label="Accepted lifecycle policies" variant="outlined"
        sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
        <ModuleOfferLifecycleTerms terms={copy.offer.lifecycle_terms} />
      </Paper>
      : <Typography variant="body2" color="text.secondary">Historical signed terms remain readable. No current lifecycle policy binding or new admission is inferred.</Typography>}
    <Paper component="section" role="region" aria-label="Company billing" variant="outlined"
      sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}><Stack spacing={1.5}>
      <Box><Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>Company billing</Typography>
        <Typography variant="body2" color="text.secondary">Review permission and keep the organization’s payment method current.</Typography></Box>
      <ModuleAgreementBilling copy={copy} billingFenceRevision={billingFenceRevision} onBillingDenied={billingDenied} onBillingRevalidated={billingRevalidated} />
    </Stack></Paper>
    <Paper component="section" role="region" aria-label="Module purchase and access" variant="outlined"
      sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}><Stack spacing={1.5}>
      <Box><Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>Purchase and access</Typography>
        <Typography variant="body2" color="text.secondary">Review the exact charge before activating this module.</Typography></Box>
      <ModuleAgreementPurchase copy={copy} billingFenceRevision={billingFenceRevision} expiryFenceRevision={expiryFenceRevision} onBillingDenied={billingDenied}
        onBillingRevalidated={billingRevalidated} onViewPurchaseRecords={viewPurchaseRecords} onReviewExpiry={reviewExpiry} />
    </Stack></Paper>
    {recordsSelection ? <Paper component="section" role="region" aria-label="Payment records" variant="outlined"
      sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}><Stack spacing={1.5}>
      <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>Payment records</Typography>
      <ModuleAgreementReceipts copy={copy} selection={recordsSelection} billingFenceRevision={billingFenceRevision}
        onReceiptDenied={billingDenied} onReceiptRevalidated={billingRevalidated} />
    </Stack></Paper> : null}
    {expirySelection ? <Paper component="section" role="region" aria-label="Module end and continuing access" variant="outlined"
      sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}><Stack spacing={1.5}>
      <Box><Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>End module</Typography>
        <Typography variant="body2" color="text.secondary">Review cutoff timing and continuing rights before ending access.</Typography></Box>
      <ModuleAgreementExpiry copy={copy} selection={expirySelection} expiryFenceRevision={expiryFenceRevision}
        onExpiryDenied={expiryDenied} onExpiryRevalidated={expiryRevalidated} />
    </Stack></Paper> : null}
  </Stack>;
}

export function ModuleAgreementPage() {
  const org = useMyOrg();
  const { user } = useAuth();
  const { acceptanceId: requestedAcceptance } = useParams<{ acceptanceId: string }>();
  const acceptanceId = canonicalModuleOfferLocator(requestedAcceptance);
  const orgId = canonicalModuleOfferLocator(org.kind === "error" ? undefined : org.org?.org_id);
  return (
    <ModuleOfferWorkspace title="Accepted offer copy">
      {org.kind === "loading" ? (
        <Typography role="status">Loading accepted copy…</Typography>
      ) : !orgId || !acceptanceId || !user?.uid ? (
        <Typography>Current Owner organization is unavailable.</Typography>
      ) : (
        <AgreementController key={`${user.uid}:${orgId}:${acceptanceId}`} orgId={orgId} acceptanceId={acceptanceId} actorId={user.uid} />
      )}
    </ModuleOfferWorkspace>
  );
}
